import { Box, Typography } from "@mui/material";
import type { OfferingOverview } from "../campaignTypes";
import {
  PORTAL_MAX_WIDTH,
  bodySx,
  cardSurfaceSx,
  eyebrowSx,
  portalFont,
  sectionTitleSx,
} from "../partnerTheme";

export default function OfferingOverviewSection({
  overview,
}: {
  overview: OfferingOverview;
}) {
  const sectionId = overview.id || "the-taping";

  return (
    <Box
      id={sectionId}
      component="section"
      sx={{
        maxWidth: PORTAL_MAX_WIDTH,
        mx: "auto",
        px: { xs: 2, md: 4 },
        pt: { xs: 7, md: 10 },
        pb: { xs: 2, md: 4 },
        scrollMarginTop: 96,
      }}
    >
      {overview.eyebrow ? (
        <Typography sx={{ ...eyebrowSx, fontSize: { xs: "0.75rem", md: "0.8125rem" } }}>
          {overview.eyebrow}
        </Typography>
      ) : null}
      <Typography
        sx={{
          ...sectionTitleSx,
          fontSize: { xs: "1.85rem", md: "2.75rem" },
          mb: { xs: 2, md: 2.5 },
        }}
      >
        {overview.title}
      </Typography>
      <Typography
        sx={{
          ...bodySx,
          maxWidth: 760,
          mb: { xs: 3.5, md: 5 },
          fontSize: { xs: "1rem", md: "1.125rem" },
          color: "var(--portal-text)",
        }}
      >
        {overview.summary}
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1.05fr 0.95fr" },
          gap: { xs: 2, md: 2.5 },
          alignItems: "stretch",
        }}
      >
        <Box
          sx={{
            ...cardSurfaceSx,
            p: { xs: 2.5, md: 3.5 },
            display: "flex",
            flexDirection: "column",
            gap: { xs: 2.25, md: 2.75 },
          }}
        >
          <Box>
            <Typography
              sx={{
                fontFamily: portalFont,
                fontSize: "0.6875rem",
                fontWeight: 700,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                color: "var(--portal-accent)",
                mb: 1,
              }}
            >
              {overview.contentEyebrow}
            </Typography>
            <Typography
              sx={{
                fontFamily: portalFont,
                fontWeight: 700,
                fontSize: { xs: "1.25rem", md: "1.5rem" },
                color: "var(--portal-text)",
                letterSpacing: "-0.02em",
                mb: 1.25,
              }}
            >
              {overview.contentTitle}
            </Typography>
            <Typography sx={{ ...bodySx, color: "var(--portal-text-muted)" }}>
              {overview.contentBody}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            {overview.hubs.map((hub) => (
              <Box
                key={hub}
                sx={{
                  px: 1.25,
                  py: 0.65,
                  borderRadius: "999px",
                  border: "1px solid color-mix(in srgb, var(--portal-accent) 45%, transparent)",
                  bgcolor: "color-mix(in srgb, var(--portal-accent) 12%, transparent)",
                  fontFamily: portalFont,
                  fontSize: "0.8125rem",
                  fontWeight: 600,
                  color: "var(--portal-text)",
                }}
              >
                {hub}
              </Box>
            ))}
          </Box>

          <Box>
            <Typography
              sx={{
                fontFamily: portalFont,
                fontSize: "0.6875rem",
                fontWeight: 700,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                color: "var(--portal-text-muted)",
                mb: 1.25,
              }}
            >
              Output
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.25, display: "grid", gap: 1.1 }}>
              {overview.outputItems.map((item) => (
                <Typography
                  key={item}
                  component="li"
                  sx={{
                    ...bodySx,
                    color: "var(--portal-text)",
                    fontSize: { xs: "0.9375rem", md: "1rem" },
                  }}
                >
                  {item}
                </Typography>
              ))}
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            ...cardSurfaceSx,
            p: { xs: 2.5, md: 3.5 },
            borderColor: "color-mix(in srgb, var(--portal-accent) 35%, var(--portal-border))",
          }}
        >
          <Typography
            sx={{
              fontFamily: portalFont,
              fontSize: "0.6875rem",
              fontWeight: 700,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: "var(--portal-accent)",
              mb: 1,
              display: overview.benefitsEyebrow ? "block" : "none",
            }}
          >
            {overview.benefitsEyebrow}
          </Typography>
          <Typography
            sx={{
              fontFamily: portalFont,
              fontWeight: 700,
              fontSize: { xs: "1.25rem", md: "1.5rem" },
              color: "var(--portal-text)",
              letterSpacing: "-0.02em",
              mb: { xs: 2, md: 2.5 },
              display: overview.benefitsTitle ? "block" : "none",
            }}
          >
            {overview.benefitsTitle}
          </Typography>
          <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none", display: "grid", gap: 1.35 }}>
            {overview.benefits.map((benefit) => (
              <Box
                key={benefit}
                component="li"
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.25,
                }}
              >
                <Box
                  aria-hidden
                  sx={{
                    mt: "0.45rem",
                    width: 8,
                    height: 8,
                    borderRadius: "2px",
                    bgcolor: "var(--portal-accent)",
                    flexShrink: 0,
                    transform: "rotate(45deg)",
                  }}
                />
                <Typography
                  sx={{
                    ...bodySx,
                    color: "var(--portal-text)",
                    fontSize: { xs: "0.9375rem", md: "1.02rem" },
                    fontWeight: 500,
                  }}
                >
                  {benefit}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
